import { FC, useContext } from "react";
import { useSearchParams } from "@remix-run/react";

import clsx from "clsx";

import MenuContext from "~/contexts/MenuContext";
import OrderContext from "~/contexts/OrderContext";
import SiteContext from "~/contexts/SiteContext";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import { getCartItemCount } from "~/helpers/cartHelpers";
import { isMenuOpen, menuHoursLabel } from "~/helpers/menuHelpers";
import { BannerAction } from "~/entities/bannerEntities";
import PlaceholderImage from "~/images/PlaceholderImage.png";
import BannersAndInfoBar from "../BannersAndInfoBar/BannersAndInfoBar";

import styles from "./DineInLanding.module.css";

/**
 * DineInLanding — the QR/dine-in entry screen (Bravo-style browse).
 * Design: docs/superpowers/specs/2026-07-23-qr-dinein-flow-design.md §4.1.
 *
 * Banner (QR-only) + a "Browse menu" grid of the store's MENUS (e.g. Food /
 * Drinks — the venue models each section as a menu; the tabs within a menu are
 * its sub-categories). Tapping a menu card opens it via the `menuCode` search
 * param — a real route, no cart reset — and the normal tabs + scrolling product
 * list renders for that menu. A sticky "View order" surfaces the running cart.
 */
type Props = {
  onBannerAction: (action: BannerAction) => void;
  onViewOrder: () => void;
};

const DineInLanding: FC<Props> = ({ onBannerAction, onViewOrder }) => {
  const { menus } = useContext(MenuContext);
  const { orderSummary } = useContext(OrderContext);
  const { site } = useContext(SiteContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);
  const [, setSearchParams] = useSearchParams();

  // A menu with availability windows is only orderable while open now (dine-in
  // is ASAP). A menu with no windows is always available. Mirrors the backend +
  // resolveMenuCode. Closed menus render greyed with a "Not available" badge and
  // aren't tappable — like Bravo's out-of-hours categories.
  const isAvailable = (m: (typeof menus)[number]) =>
    !m.availability?.length || isMenuOpen(m, null, site?.timeZone);

  const accent = template.global.button.primaryButtonColour;
  const accentFg = template.global.button.primaryButtonFontColour;

  const itemCount = getCartItemCount(orderSummary.lines);

  const openMenu = (code: string) =>
    setSearchParams((x) => {
      x.set("menuCode", code);
      return x;
    });

  return (
    <div className={styles.wrapper}>
      <BannersAndInfoBar onBannerAction={onBannerAction} />

      <div className={styles.body}>
        <h2 className={styles.heading}>Browse menu</h2>
        <div className={styles.grid}>
          {(menus ?? []).map((m) => {
            const available = isAvailable(m);
            const hours = menuHoursLabel(m, site?.timeZone);
            return (
              <button
                key={m.code}
                type="button"
                disabled={!available}
                className={clsx(
                  styles.card,
                  !available && styles.cardUnavailable,
                )}
                onClick={() => available && openMenu(m.code)}
              >
                {/* Placeholder art until menus carry an image (backend/admin). */}
                <img className={styles.cardImg} src={PlaceholderImage} alt="" />
                <span className={styles.cardScrim} />
                {!available && (
                  <span className={styles.unavailableBadge}>Not available</span>
                )}
                <span className={styles.cardText}>
                  <span className={styles.cardName}>{m.name}</span>
                  {available && hours && (
                    <span className={styles.cardHours}>{hours}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {itemCount > 0 && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.viewOrder}
            style={{ background: accent, color: accentFg }}
            onClick={onViewOrder}
          >
            <span
              className={styles.count}
              style={{ color: accent, background: accentFg }}
            >
              {itemCount}
            </span>
            <span>View order</span>
            <b>{formatAmount(orderSummary?.finalPriceIncTax ?? 0)}</b>
          </button>
        </div>
      )}
    </div>
  );
};

export default DineInLanding;
